import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check, X, Trash2, Plus, LogOut } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fileToCompressedDataURL } from "@/lib/image-utils";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/Logo";
import { Button } from "@/components/UI";

export default function AdminDashboard() {
  const { user, checked, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("registry");

  useEffect(() => {
    if (checked && (!user || user.role !== "admin")) nav("/admin/login");
  }, [checked, user, nav]);

  if (!checked || !user) return <div className="min-h-screen flex items-center justify-center bg-ccpm-ivory"><Loader2 className="animate-spin text-ccpm-olive" /></div>;

  return (
    <div className="min-h-screen bg-ccpm-stone">
      <header className="bg-ccpm-charcoal text-ccpm-ivory">
        <div className="mx-auto max-w-6xl px-6 h-20 flex items-center justify-between">
          <Wordmark light />
          <div className="flex items-center gap-6">
            <span className="text-sm text-ccpm-olive-soft hidden sm:inline">{user.email}</span>
            <button onClick={async () => { await logout(); nav("/admin/login"); }} className="inline-flex items-center gap-2 text-sm hover:text-ccpm-olive-soft" data-testid="admin-logout"><LogOut size={16} /> Sign out</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-12">
        <h1 className="font-serif text-4xl text-ccpm-charcoal mb-8">Council Dashboard</h1>
        <div className="flex gap-2 mb-10 border-b border-ccpm-border flex-wrap">
          {[["registry", "Practitioner Registry"], ["news", "News & Advocacy"], ["consultation", "Consultation"], ["work", "Our Work"], ["waitlist", "Textbook Waitlist"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-ccpm-olive text-ccpm-olive" : "border-transparent text-ccpm-taupe hover:text-ccpm-charcoal"}`}>{label}</button>
          ))}
        </div>
        {tab === "registry" ? <RegistryAdmin /> : tab === "news" ? <NewsAdmin /> : tab === "consultation" ? <CommentsAdmin /> : tab === "waitlist" ? <WaitlistAdmin /> : <WorkAdmin />}
      </div>
    </div>
  );
}

function RegistryAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => { setLoading(true); try { const { data } = await api.get("/admin/practitioners"); setItems(data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => { await api.patch(`/admin/practitioners/${id}`, { status }); toast.success(`Marked ${status}`); load(); };
  const del = async (id) => { await api.delete(`/admin/practitioners/${id}`); toast.success("Removed"); load(); };

  if (loading) return <Loader2 className="animate-spin text-ccpm-olive" />;
  return (
    <div className="space-y-3" data-testid="admin-registry-list">
      {items.map((p) => (
        <div key={p.id} className="bg-white border border-ccpm-border p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`admin-practitioner-${p.id}`}>
          <div>
            <div className="flex items-center gap-3">
              <h3 className="font-serif text-xl text-ccpm-charcoal">{p.name}</h3>
              <span className={`text-[11px] px-2 py-0.5 font-semibold uppercase tracking-wide ${p.status === "verified" ? "bg-ccpm-olive text-ccpm-ivory" : p.status === "pending" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}>{p.status}</span>
            </div>
            <p className="text-sm text-ccpm-taupe mt-1">{p.clinic_name} · {p.city}, {p.province} · {p.email}</p>
          </div>
          <div className="flex items-center gap-2">
            {p.status !== "verified" && <button onClick={() => setStatus(p.id, "verified")} className="p-2 border border-ccpm-border hover:border-ccpm-olive text-ccpm-olive" title="Verify" data-testid={`verify-${p.id}`}><Check size={16} /></button>}
            {p.status !== "rejected" && <button onClick={() => setStatus(p.id, "rejected")} className="p-2 border border-ccpm-border hover:border-red-400 text-red-600" title="Reject"><X size={16} /></button>}
            <button onClick={() => del(p.id)} className="p-2 border border-ccpm-border hover:border-red-400 text-red-600" title="Delete"><Trash2 size={16} /></button>
          </div>
        </div>
      ))}
      {items.length === 0 && <p className="text-ccpm-taupe">No applications yet.</p>}
    </div>
  );
}

function NewsAdmin() {
  const empty = { title: "", excerpt: "", body: "", category: "News", author: "CCPM", cover_image: "", published: true };
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => { setLoading(true); try { const { data } = await api.get("/admin/news"); setItems(data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/admin/news/${editing}`, form); toast.success("Article updated"); }
      else { await api.post("/admin/news", form); toast.success("Article published"); }
      setForm(empty); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const edit = (a) => { setEditing(a.id); setForm({ title: a.title, excerpt: a.excerpt, body: a.body, category: a.category, author: a.author, cover_image: a.cover_image || "", published: a.published }); window.scrollTo(0, 0); };
  const del = async (id) => { await api.delete(`/admin/news/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <form onSubmit={save} className="bg-white border border-ccpm-border p-7 h-fit" data-testid="admin-news-form">
        <h3 className="font-serif text-2xl text-ccpm-charcoal mb-6 flex items-center gap-2"><Plus size={18} className="text-ccpm-olive" />{editing ? "Edit article" : "New article"}</h3>
        <div className="space-y-4">
          <input required placeholder="Title" value={form.title} onChange={set("title")} className="admin-input" data-testid="news-title" />
          <div className="grid grid-cols-2 gap-4">
            <input placeholder="Category" value={form.category} onChange={set("category")} className="admin-input" />
            <input placeholder="Author" value={form.author} onChange={set("author")} className="admin-input" />
          </div>
          <input placeholder="Cover image URL" value={form.cover_image} onChange={set("cover_image")} className="admin-input" />
          <textarea required placeholder="Excerpt" rows={2} value={form.excerpt} onChange={set("excerpt")} className="admin-input resize-none" data-testid="news-excerpt" />
          <textarea required placeholder="Body (use blank lines for paragraphs)" rows={8} value={form.body} onChange={set("body")} className="admin-input resize-none" data-testid="news-body" />
          <label className="flex items-center gap-2 text-sm text-ccpm-taupe"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published</label>
        </div>
        <div className="flex gap-3 mt-6">
          <Button type="submit" data-testid="news-save">{editing ? "Update" : "Publish"}</Button>
          {editing && <Button variant="outline" onClick={() => { setEditing(null); setForm(empty); }}>Cancel</Button>}
        </div>
      </form>

      <div className="space-y-3" data-testid="admin-news-list">
        {loading ? <Loader2 className="animate-spin text-ccpm-olive" /> : items.map((a) => (
          <div key={a.id} className="bg-white border border-ccpm-border p-5">
            <div className="flex justify-between gap-3">
              <div>
                <span className="overline text-ccpm-olive">{a.category}</span>
                <h4 className="font-serif text-xl text-ccpm-charcoal mt-1">{a.title}</h4>
                {!a.published && <span className="text-[11px] text-amber-700">Draft</span>}
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => edit(a)} className="text-xs text-ccpm-olive hover:underline" data-testid={`edit-news-${a.id}`}>Edit</button>
                <button onClick={() => del(a.id)} className="text-xs text-red-600 hover:underline" data-testid={`delete-news-${a.id}`}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <style>{`.admin-input{width:100%;padding:0.7rem 0.85rem;background:#FDFBF7;border:1px solid #E6DFD9;font-size:0.875rem;color:#2B2B2B;outline:none}.admin-input:focus{border-color:#6B705C}`}</style>
    </div>
  );
}

function CommentsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => { setLoading(true); try { const { data } = await api.get("/admin/consultation/comments"); setItems(data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await api.patch(`/admin/consultation/comments/${id}`, { status }); toast.success(status === "approved" ? "Published" : "Unpublished"); load(); };
  const del = async (id) => { await api.delete(`/admin/consultation/comments/${id}`); toast.success("Deleted"); load(); };

  if (loading) return <Loader2 className="animate-spin text-ccpm-olive" />;
  const pending = items.filter((c) => c.status !== "approved");
  return (
    <div className="space-y-3" data-testid="admin-comments-list">
      {items.length > 0 && <p className="text-sm text-ccpm-taupe mb-4">{pending.length} awaiting review · {items.length - pending.length} published</p>}
      {items.map((c) => (
        <div key={c.id} className="bg-white border border-ccpm-border p-5" data-testid={`admin-comment-${c.id}`}>
          <div className="flex justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-serif text-xl text-ccpm-charcoal">{c.name}</h3>
                <span className={`text-[11px] px-2 py-0.5 font-semibold uppercase tracking-wide ${c.status === "approved" ? "bg-ccpm-olive text-ccpm-ivory" : "bg-amber-100 text-amber-800"}`}>{c.status === "approved" ? "Published" : "Pending"}</span>
              </div>
              {(c.role || c.organization) && <p className="text-xs text-ccpm-taupe mt-0.5">{[c.role, c.organization].filter(Boolean).join(" · ")}{c.email ? ` · ${c.email}` : ""}</p>}
              <p className="mt-3 text-sm text-ccpm-charcoal/90 font-light leading-relaxed">{c.comment}</p>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              {c.status !== "approved"
                ? <button onClick={() => setStatus(c.id, "approved")} className="text-xs font-semibold text-ccpm-olive hover:underline" data-testid={`approve-comment-${c.id}`}>Approve</button>
                : <button onClick={() => setStatus(c.id, "pending")} className="text-xs text-ccpm-taupe hover:underline" data-testid={`unpublish-comment-${c.id}`}>Unpublish</button>}
              <button onClick={() => del(c.id)} className="text-xs text-red-600 hover:underline" data-testid={`delete-comment-${c.id}`}>Delete</button>
            </div>
          </div>
        </div>
      ))}
      {items.length === 0 && <p className="text-ccpm-taupe">No consultation comments yet.</p>}
    </div>
  );
}

function WaitlistAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => { try { const { data } = await api.get("/admin/textbook/waitlist"); setItems(data); } finally { setLoading(false); } })(); }, []);

  if (loading) return <Loader2 className="animate-spin text-ccpm-olive" />;
  return (
    <div data-testid="admin-waitlist-list">
      <p className="text-sm text-ccpm-taupe mb-5">{items.length} {items.length === 1 ? "person is" : "people are"} waiting to be notified when the textbook is released.</p>
      <div className="bg-white border border-ccpm-border divide-y divide-ccpm-border">
        {items.map((w) => (
          <div key={w.id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-waitlist-${w.id}`}>
            <div>
              <h4 className="font-serif text-lg text-ccpm-charcoal">{w.name || "—"}</h4>
              <p className="text-sm text-ccpm-taupe">{w.email}</p>
            </div>
            <span className="text-xs text-ccpm-taupe">{new Date(w.created_at).toLocaleDateString()}</span>
          </div>
        ))}
      </div>
      {items.length === 0 && <p className="text-ccpm-taupe">No waitlist signups yet.</p>}
    </div>
  );
}

function WorkAdmin() {
  const CATS = ["3D Areola Restoration", "Scar Camouflage", "Vitiligo Camouflage", "Burn Scar Restoration", "Other"];
  const empty = { title: "", category: CATS[0], description: "", before_image: "", after_image: "", consent: false, featured: false, story: "", stages: [] };
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const load = async () => { setLoading(true); try { const { data } = await api.get("/work"); setItems(data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFile = (key) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await fileToCompressedDataURL(file);
      setForm((f) => ({ ...f, [key]: dataUrl }));
    } catch { toast.error("Could not process that image."); }
  };

  const addStage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const image = await fileToCompressedDataURL(file, 1000);
      setForm((f) => ({ ...f, stages: [...f.stages, { image, caption: "" }] }));
    } catch { toast.error("Could not process that image."); }
    e.target.value = "";
  };
  const setStageCaption = (idx, val) => setForm((f) => ({ ...f, stages: f.stages.map((s, i) => i === idx ? { ...s, caption: val } : s) }));
  const removeStage = (idx) => setForm((f) => ({ ...f, stages: f.stages.filter((_, i) => i !== idx) }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.after_image) return toast.error("Please add the result image.");
    if (!form.consent) return toast.error("Please confirm you have consent to publish these images.");
    setBusy(true);
    try {
      await api.post("/admin/work", form);
      toast.success("Case published to the gallery");
      setForm(empty); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save."); }
    finally { setBusy(false); }
  };
  const del = async (id) => { await api.delete(`/admin/work/${id}`); toast.success("Removed"); load(); };

  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <form onSubmit={save} className="bg-white border border-ccpm-border p-7 h-fit" data-testid="admin-work-form">
        <h3 className="font-serif text-2xl text-ccpm-charcoal mb-6 flex items-center gap-2"><Plus size={18} className="text-ccpm-olive" />Add a before / after case</h3>
        <div className="space-y-4">
          <input required placeholder="Case title" value={form.title} onChange={set("title")} className="admin-input" data-testid="work-title" />
          <select value={form.category} onChange={set("category")} className="admin-input" data-testid="work-category">
            {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <textarea placeholder="Short description (optional)" rows={2} value={form.description} onChange={set("description")} className="admin-input resize-none" data-testid="work-description" />
          <textarea placeholder="Case study story (optional — blank lines separate paragraphs)" rows={5} value={form.story} onChange={set("story")} className="admin-input resize-none" data-testid="work-story" />
          <div className="grid grid-cols-2 gap-4">
            {["before_image", "after_image"].map((k, idx) => (
              <div key={k}>
                <label className="overline text-ccpm-taupe block mb-2">{idx === 0 ? "Before (optional)" : "Result image (required)"}</label>
                <div className="aspect-[4/3] bg-ccpm-stone border border-ccpm-border overflow-hidden mb-2 flex items-center justify-center text-xs text-ccpm-taupe">
                  {form[k] ? <img src={form[k]} alt={k} className="w-full h-full object-cover" /> : "No image"}
                </div>
                <input type="file" accept="image/*" onChange={onFile(k)} className="text-xs" data-testid={`work-${idx === 0 ? "before" : "after"}`} />
              </div>
            ))}
          </div>
          <p className="text-xs text-ccpm-taupe">Add both a Before and Result image to enable the drag-to-compare slider, or just a Result image for a single photo (including before/after composites).</p>

          <div className="border-t border-ccpm-border pt-4">
            <div className="flex items-center justify-between mb-3">
              <span className="overline text-ccpm-taupe">Case-study stages (optional)</span>
              <label className="text-xs font-semibold text-ccpm-olive cursor-pointer hover:underline">
                + Add stage image
                <input type="file" accept="image/*" onChange={addStage} className="hidden" data-testid="work-add-stage" />
              </label>
            </div>
            {form.stages.length === 0 && <p className="text-xs text-ccpm-taupe">Add images in order (e.g. mapping → colour match → procedure → healed → refined) to tell the story.</p>}
            <div className="space-y-3" data-testid="work-stages">
              {form.stages.map((s, i) => (
                <div key={i} className="flex gap-3 items-start bg-ccpm-stone p-2 border border-ccpm-border">
                  <span className="font-serif text-lg text-ccpm-olive-soft w-6 text-center shrink-0">{i + 1}</span>
                  <img src={s.image} alt={`stage ${i + 1}`} className="w-16 h-16 object-cover shrink-0 border border-ccpm-border" />
                  <input placeholder="Caption for this stage" value={s.caption} onChange={(e) => setStageCaption(i, e.target.value)} className="admin-input flex-1" data-testid={`work-stage-caption-${i}`} />
                  <button type="button" onClick={() => removeStage(i)} className="text-xs text-red-600 hover:underline shrink-0 mt-2" data-testid={`work-stage-remove-${i}`}>Remove</button>
                </div>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-ccpm-taupe"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="work-featured" /> Feature this case at the top of the gallery</label>
          <label className="flex items-start gap-2 text-sm text-ccpm-taupe"><input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} data-testid="work-consent" className="mt-1" /> I confirm I have the informed consent of the individual(s) shown to publish these images.</label>
        </div>
        <Button type="submit" disabled={busy} className="mt-6" data-testid="work-save">{busy ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : "Publish case"}</Button>
      </form>

      <div className="space-y-3" data-testid="admin-work-list">
        {loading ? <Loader2 className="animate-spin text-ccpm-olive" /> : items.map((c) => (
          <div key={c.id} className="bg-white border border-ccpm-border p-4 flex items-center gap-4" data-testid={`admin-work-${c.id}`}>
            <img src={c.after_image} alt={c.title} className="w-16 h-16 object-cover shrink-0 border border-ccpm-border" />
            <div className="min-w-0 flex-1">
              <span className="overline text-ccpm-olive">{c.category}</span>
              <h4 className="font-serif text-lg text-ccpm-charcoal truncate">{c.title}</h4>
            </div>
            <button onClick={() => del(c.id)} className="text-xs text-red-600 hover:underline shrink-0" data-testid={`delete-work-${c.id}`}>Delete</button>
          </div>
        ))}
        {!loading && items.length === 0 && <p className="text-ccpm-taupe">No cases published yet.</p>}
      </div>
      <style>{`.admin-input{width:100%;padding:0.7rem 0.85rem;background:#FDFBF7;border:1px solid #E6DFD9;font-size:0.875rem;color:#2B2B2B;outline:none}.admin-input:focus{border-color:#6B705C}`}</style>
    </div>
  );
}
